<template>
  <div :class="$style.container">
    <h3 :class="$style.title">日程調整ごとのデフォルト設定</h3>

    <div :class="$style.formContainer">
      <table :class="$style.settingsTable">
        <thead>
          <tr>
            <th>日程調整タイプ</th>
            <th>使用カレンダー</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="variant in scheduleAdjustmentVariants"
            :key="variant.value"
          >
            <td :class="$style.variantLabel">{{ variant.label }}</td>
            <td>
              <div :class="$style.calendarSelector">
                <DolphinSelector
                  v-model="formData[variant.value]"
                  :options="calendarOptions"
                  :class="$style.selector"
                  :invalid="
                    variant.value === ScheduleAdjustmentVariant.Default &&
                    !formData[variant.value]
                  "
                />
                <ButtonIconOnly
                  v-if="
                    formData[variant.value] &&
                    variant.value !== ScheduleAdjustmentVariant.Default
                  "
                  :class="$style.deleteButton"
                  @click="clearCalendarSelection(variant.value)"
                >
                  <template #icon><IconTrash /></template>
                </ButtonIconOnly>
                <span
                  v-if="
                    getCalendarDisplayInfo(formData[variant.value]).isOrphaned
                  "
                  :class="$style.orphanedCalendarText"
                >
                  {{ getCalendarDisplayInfo(formData[variant.value]).name }} -
                  再設定してください
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div :class="$style.actions">
      <FlatButton variant="base" @click="$emit('cancel')"
        >キャンセル</FlatButton
      >
      <FlatButton variant="primary" @click="handleSave" :disabled="!isFormValid"
        >保存する</FlatButton
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import ButtonIconOnly from '@/components/atoms/ButtonIconOnly.vue'
import IconTrash from '@/components/icons/IconTrash.vue'
import { ScheduleAdjustmentVariant } from '@/types/schema.generated.ts'

import type {
  ScheduleAdjustmentDefaultCalendarInput,
  ScheduleAdjustmentVariant as ScheduleAdjustmentVariantType,
} from '@/types/schema.generated.ts'
import type { Option } from '@/types/app'
import { scheduleAdjustmentVariants } from './calendar.ts'

const props = defineProps<{
  defaultCalendars: ScheduleAdjustmentDefaultCalendarInput[]
  availableCalendars: {
    guid: string
    publicTitle: string
    privateTitle?: string
  }[]
}>()

const emit = defineEmits<{
  (e: 'save', defaultCalendars: ScheduleAdjustmentDefaultCalendarInput[]): void
  (e: 'cancel'): void
}>()

const formData = ref<Record<ScheduleAdjustmentVariantType, string>>(
  Object.fromEntries(
    scheduleAdjustmentVariants.map(v => {
      const defaultCalendar = props.defaultCalendars.find(
        calendar => calendar.ScheduleAdjustmentVariant === v.value
      )

      return [v.value, defaultCalendar?.CalendarGUID || '']
    })
  ) as Record<ScheduleAdjustmentVariantType, string>
)

const isFormValid = computed(() => {
  // デフォルト設定は必須
  return !!formData.value[ScheduleAdjustmentVariant.Default]
})

const getCalendarDisplayInfo = (
  calendarGUID: string | null
): {
  name: string | null
  isOrphaned: boolean
  originalGuid: string | null
} => {
  if (!calendarGUID) {
    return { name: null, isOrphaned: false, originalGuid: calendarGUID }
  }
  const calendar = props.availableCalendars.find(c => c.guid === calendarGUID)
  if (calendar) {
    return {
      name: calendar.privateTitle
        ? calendar.publicTitle + ` (${calendar.privateTitle})`
        : calendar.publicTitle,
      isOrphaned: false,
      originalGuid: calendarGUID,
    }
  }
  return {
    name: '（削除されたカレンダー）',
    isOrphaned: true,
    originalGuid: calendarGUID,
  }
}

const calendarOptions = computed((): Option<string>[] => {
  return props.availableCalendars.map(c => ({
    value: c.guid,
    text: c.privateTitle
      ? c.publicTitle + ` (${c.privateTitle})`
      : c.publicTitle,
  }))
})

const handleSave = () => {
  const updatedDefaultCalendars: ScheduleAdjustmentDefaultCalendarInput[] =
    Object.entries(formData.value)
      .filter(([_, calendarGUID]) => calendarGUID !== '')
      .map(([variant, calendarGUID]) => ({
        ScheduleAdjustmentVariant: variant as ScheduleAdjustmentVariantType,
        CalendarGUID: calendarGUID as string,
      }))

  emit('save', updatedDefaultCalendars)
}

const clearCalendarSelection = (
  variantValue: ScheduleAdjustmentVariantType
) => {
  // デフォルトの場合はクリアしない（必須項目のため）
  if (variantValue === ScheduleAdjustmentVariant.Default) {
    return
  }

  formData.value[variantValue] = ''
}
</script>

<style lang="scss" module>
.container {
  padding: 24px;
}

.title {
  font-size: 18px;
  font-weight: bold;
  margin: 0 0 24px;
}

.formContainer {
  margin-bottom: 24px;
}

.settingsTable {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid $color-border-low-emphasis;
  }

  th {
    font-weight: bold;
    color: $color-text-and-object-high-emphasis;
  }

  td {
    color: $color-text-and-object-medium-emphasis;
  }
}

.variantLabel {
  width: 150px;
  font-weight: bold;
}

.calendarSelector {
  display: flex;
  align-items: center;
  width: 100%;
}

.selector {
  width: 300px;
}

.deleteButton {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  color: $color-caution-accent;
  width: 32px;
  height: 32px;
  border: 1px solid $color-caution-accent;
  border-radius: 4px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.orphanedCalendarText {
  margin-left: 8px;
  color: $color-text-and-object-accent-red;
}
</style>
